/**
 * The pages, as pictures of themselves, along the bottom of the designer.
 *
 * A product with two sides or sixteen booklet pages was navigable only through
 * a menu of names - "Front", "Page 7" - which tells a customer nothing about
 * which page they want. A picture does, and it is the one control everybody
 * already knows from every other design tool.
 *
 * Only shown when there is more than one page. On a business card or a canvas
 * a strip of one thumbnail is a row of wasted height.
 */

import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import { addPage } from '../state/actions';
import { pagesFixed } from '../api';
import { mmToPx } from '../model/units';
import { FIT_TOP } from '../canvas/stack';

/** Longest side of a thumbnail, px. Small enough to draw often, big enough to recognise. */
const THUMB = 150;

/** How long after the last edit the pictures are brought up to date. */
const SETTLE_MS = 600;

export function PageStrip() {
  const doc = useEditor( ( s ) => s.doc );
  const pageIndex = useEditor( ( s ) => s.pageIndex );
  const [ shots, setShots ] = useState<Record<string, string>>( {} );

  /*
   * What each page looked like when it was last drawn. Pages are redrawn only
   * when their own contents change, so editing page one does not re-render the
   * other fifteen on every keystroke.
   */
  const drawn = useRef( new Map<string, unknown>() );
  const alive = useRef( true );

  useEffect( () => () => { alive.current = false; }, [] );

  useEffect( () => {
    if ( ! doc || doc.pages.length < 2 ) return;

    const timer = window.setTimeout( async () => {
      const { pageTexture } = await import( '../three/texture' );
      const current = useEditor.getState().doc;

      if ( ! current ) return;

      for ( let index = 0; index < current.pages.length; index++ ) {
        const page = current.pages[ index ]!;

        if ( drawn.current.get( page.id ) === page ) continue;

        const canvas = await pageTexture( current, index, THUMB );

        if ( ! canvas || ! alive.current ) return;

        drawn.current.set( page.id, page );
        setShots( ( was ) => ( { ...was, [ page.id ]: canvas.toDataURL( 'image/png' ) } ) );
      }
    }, SETTLE_MS );

    return () => window.clearTimeout( timer );
  }, [ doc ] );

  if ( ! doc || doc.pages.length < 2 ) return null;

  /** Makes a page the active one, and puts its top in view, as the menu does. */
  const show = ( index: number ) => {
    const store = useEditor.getState();

    store.goToPage( index );
    store.setView( { y: FIT_TOP + mmToPx( doc.bleed, store.view.zoom ) } );
  };

  return (
    <div className="tb-strip" role="tablist" aria-label="Pages">
      { doc.pages.map( ( page, index ) => {
        const tall = page.size.h > page.size.w;
        const shot = shots[ page.id ];

        return (
          <button
            key={ page.id }
            type="button"
            role="tab"
            aria-selected={ index === pageIndex }
            aria-label={ `${ index + 1 }. ${ page.name }` }
            className={ `tb-strip__page${ index === pageIndex ? ' is-on' : '' }` }
            onClick={ () => show( index ) }
          >
            <span
              className="tb-strip__shot"
              /* The frame keeps the page's own shape, so a landscape page reads as landscape before it has drawn. */
              style={ { aspectRatio: `${ page.size.w } / ${ page.size.h }`, width: tall ? 'auto' : '100%', height: tall ? '100%' : 'auto' } }
            >
              { shot ? <img src={ shot } alt="" /> : <span className="tb-strip__blank" aria-hidden="true" /> }
            </span>
            <span className="tb-strip__name">
              <span className="tb-num">{ index + 1 }</span> { page.name }
            </span>
          </button>
        );
      } ) }

      { /* Only where the product has not already decided what its pages are. */ }
      { ! pagesFixed() && (
        <button type="button" className="tb-strip__add" onClick={ () => addPage() } aria-label="Add a page">
          +
        </button>
      ) }
    </div>
  );
}
